// CertificateMockup — pure HTML/CSS recreation of the ShansoAI course certificate
// PDF generated by /app/backend/routers/shanso_learn.py::_generate_course_cert_pdf
//
// Used on CourseDetailPage to give learners an aspirational preview of what
// they'll earn. Two render modes:
//   - tilted (default): subtle 3D tilt + float, hover-lift, "Tap to preview" overlay
//   - flat (modal): un-tilted, full-size lightbox view
//
// Pure CSS — no canvas/SVG/PDF — so it scales crisply and is text-selectable.

import { useState } from "react";
import { X, ZoomIn, Award } from "lucide-react";

const NAVY = "#0A1323";
const NAVY_SOFT = "#283246";
const CYAN = "#22D3EE";
const CYAN_DEEP = "#0891B2";
const MUTED = "#6B7280";

function HexBadge({ role }) {
  // Mirror the PDF hex badge: outer white hex with navy stroke, inner cyan hex with cyan_deep stroke.
  const words = (role || "Skill").toUpperCase().split(" ");
  let lines;
  if (words.length === 1) lines = [words[0]];
  else if (words.length === 2) lines = words;
  else {
    const mid = Math.floor(words.length / 2);
    lines = [words.slice(0, mid).join(" "), words.slice(mid).join(" ")];
  }
  // Auto-shrink font for long lines
  const longest = Math.max(...lines.map(l => l.length));
  const roleFontPx = longest > 10 ? 7 : longest > 7 ? 8 : 9;

  return (
    <div className="absolute top-[6%] right-[5%] w-[15%] aspect-square pointer-events-none">
      {/* Outer hex (white fill, navy stroke) — clipPath hex shape via SVG */}
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <defs>
          <polygon id="hex" points="50,5 91,27 91,73 50,95 9,73 9,27" />
        </defs>
        <use href="#hex" fill="#fff" stroke={NAVY} strokeWidth="2" />
        {/* Inner hex (cyan fill, cyan_deep stroke) */}
        <polygon
          points="50,18 78,33 78,67 50,82 22,67 22,33"
          fill={CYAN}
          stroke={CYAN_DEEP}
          strokeWidth="1"
        />
        {/* CERTIFIED top label */}
        <text x="50" y="34" textAnchor="middle" fontSize="6.5" fontWeight="700" fill={NAVY} fontFamily="Helvetica, Arial, sans-serif">
          CERTIFIED
        </text>
        {/* Role */}
        {lines.length === 1 ? (
          <text x="50" y="52" textAnchor="middle" fontSize={roleFontPx} fontWeight="700" fill={NAVY} fontFamily="Helvetica, Arial, sans-serif">
            {lines[0]}
          </text>
        ) : (
          <>
            <text x="50" y="48" textAnchor="middle" fontSize={roleFontPx} fontWeight="700" fill={NAVY} fontFamily="Helvetica, Arial, sans-serif">
              {lines[0]}
            </text>
            <text x="50" y="58" textAnchor="middle" fontSize={roleFontPx} fontWeight="700" fill={NAVY} fontFamily="Helvetica, Arial, sans-serif">
              {lines[1]}
            </text>
          </>
        )}
        {/* shansoai.com bottom label */}
        <text x="50" y="73" textAnchor="middle" fontSize="5" fill={NAVY_SOFT} fontFamily="Helvetica, Arial, sans-serif">
          shansoai.com
        </text>
      </svg>
    </div>
  );
}

function CertificateBody({ course, recipientName }) {
  // Build a short hex-badge role: prefer explicit cert_role, else derive a tidy
  // 2-3 word title from the course title. Never fall back to the stale
  // certificate_template default which is hard-coded to "Prompt Engineering".
  let role = (course?.cert_role || "").toString().trim();
  if (!role && course?.title) {
    const t = course.title
      .replace(/^(AI|the)\s+/i, "")
      .replace(/[:\-—].*$/, "")
      .replace(/\bfor\b.*$/i, "")
      .replace(/\bquickstart\b/i, "Quickstart")
      .trim();
    const words = t.split(/\s+/).filter(Boolean).slice(0, 3);
    role = words.join(" ") || "Practitioner";
  }
  if (!role) role = "Practitioner";
  const courseTitle = course?.title || "this course";
  const bodyDesc =
    course?.cert_body_description ||
    `has successfully completed ${courseTitle} and demonstrated practical mastery of the core concepts, frameworks, and tools covered across all course modules.`;

  // Split body description around the course title so we can highlight it in cyan_deep.
  let bodyParts;
  if (courseTitle && bodyDesc.includes(courseTitle)) {
    const [before, ...rest] = bodyDesc.split(courseTitle);
    const after = rest.join(courseTitle);
    bodyParts = (
      <>
        <span style={{ color: NAVY_SOFT }}>{before}</span>
        <span style={{ color: CYAN_DEEP, fontWeight: 700 }}>{courseTitle}</span>
        <span style={{ color: NAVY_SOFT }}>{after}</span>
      </>
    );
  } else {
    bodyParts = <span style={{ color: NAVY_SOFT }}>{bodyDesc}</span>;
  }

  const displayName = (recipientName || "[Your Name]").toUpperCase();
  const issued = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });

  return (
    <div className="relative w-full h-full bg-white overflow-hidden font-sans" style={{ color: NAVY }}>
      {/* Subtle cyan dot grid (CSS radial-gradient pattern) */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(${CYAN} 0.6px, transparent 0.7px)`,
          backgroundSize: "14px 14px",
          opacity: 0.18,
        }}
      />

      {/* Top-right cyan diagonal triangles (layered) */}
      <div
        className="absolute top-0 right-0 pointer-events-none"
        style={{
          width: "44%",
          aspectRatio: "1 / 1",
          background: CYAN,
          clipPath: "polygon(100% 0, 0 0, 100% 100%)",
        }}
      />
      <div
        className="absolute top-0 right-0 pointer-events-none"
        style={{
          width: "30%",
          aspectRatio: "1 / 1",
          background: CYAN_DEEP,
          clipPath: "polygon(100% 0, 0 0, 100% 100%)",
        }}
      />

      {/* Bottom-left navy triangle (with smaller cyan accent on top) */}
      <div
        className="absolute bottom-0 left-0 pointer-events-none"
        style={{
          width: "27%",
          aspectRatio: "1 / 1",
          background: NAVY,
          clipPath: "polygon(0 100%, 0 0, 100% 100%)",
        }}
      />
      <div
        className="absolute bottom-0 left-0 pointer-events-none"
        style={{
          width: "8.5%",
          aspectRatio: "1 / 1",
          background: CYAN,
          clipPath: "polygon(0 100%, 0 0, 100% 100%)",
        }}
      />

      {/* Top-left shansoai.com wordmark */}
      <div className="absolute top-[7%] left-[6%] flex items-center gap-2">
        <div className="w-2 h-2 rounded-full" style={{ background: CYAN }} />
        <div>
          <p className="font-bold text-[clamp(11px,1.55cqi,22px)] tracking-tight leading-none" style={{ color: NAVY }}>
            shansoai.com
          </p>
          <p className="text-[clamp(7px,0.85cqi,11px)] mt-1 tracking-wider" style={{ color: MUTED }}>
            Learn · Build · Ship
          </p>
        </div>
      </div>

      {/* Top-right hex badge */}
      <HexBadge role={role} />

      {/* CENTRAL HIERARCHY */}
      <div className="absolute inset-0 flex flex-col items-center pt-[26%]">
        {/* CERTIFICATE OF ACHIEVEMENT */}
        <div className="relative">
          <h2
            className="font-extrabold tracking-tight text-center"
            style={{ color: NAVY, fontSize: "clamp(18px, 3.1cqi, 44px)", letterSpacing: "0.01em" }}
          >
            CERTIFICATE OF ACHIEVEMENT
          </h2>
          <div className="flex items-center justify-center mt-1">
            <div style={{ height: 2, width: "100%", background: CYAN, minWidth: 60 }} />
          </div>
          <div className="flex justify-center -mt-[3px]">
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: CYAN_DEEP }} />
          </div>
        </div>

        {/* "This is to certify that" */}
        <p
          className="italic mt-[4.5%] text-center"
          style={{ color: MUTED, fontSize: "clamp(9px, 1.35cqi, 18px)" }}
        >
          This is to certify that
        </p>

        {/* RECIPIENT NAME */}
        <h3
          className="font-extrabold uppercase tracking-tight mt-[3%] text-center px-[10%]"
          style={{ color: NAVY, fontSize: "clamp(20px, 3.4cqi, 48px)", letterSpacing: "0.02em" }}
        >
          {displayName}
        </h3>
        <div className="mt-1" style={{ height: 1.5, width: "55%", background: CYAN }} />

        {/* Body description */}
        <p
          className="text-center px-[15%] mt-[4%] leading-relaxed"
          style={{ fontSize: "clamp(8px, 1.15cqi, 15px)" }}
        >
          {bodyParts}
        </p>
      </div>

      {/* Bottom-right issued date */}
      <div className="absolute bottom-[7%] right-[6%] text-right">
        <p className="text-[clamp(7px,0.85cqi,11px)] uppercase tracking-widest" style={{ color: MUTED }}>
          Issued
        </p>
        <p className="font-bold text-[clamp(9px,1.1cqi,14px)] mt-0.5" style={{ color: NAVY }}>
          {issued}
        </p>
      </div>
    </div>
  );
}

/* Public: Tilted, floating mockup that opens a flat preview modal on click */
export default function CertificateMockup({ course, recipientName }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* ─── Inline tilted card ─── */}
      <div className="w-full" style={{ perspective: "1400px" }}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="group relative w-full block rounded-2xl shadow-[0_30px_80px_-30px_rgba(34,211,238,0.35)] focus:outline-none focus:ring-2 focus:ring-cyan-400/60"
          style={{
            aspectRatio: "1.414 / 1",
            transformStyle: "preserve-3d",
            transition: "transform 700ms cubic-bezier(.2,.7,.2,1), box-shadow 700ms",
            animation: "cert-float 6s ease-in-out infinite",
            containerType: "inline-size",
          }}
          data-testid="cert-mockup-tilt"
          aria-label="Preview the certificate you'll earn"
        >
          <div
            className="absolute inset-0 rounded-2xl overflow-hidden border"
            style={{
              borderColor: "rgba(34,211,238,0.25)",
              transform: "rotateY(-9deg) rotateX(6deg) rotate(-1.2deg)",
              transition: "transform 700ms cubic-bezier(.2,.7,.2,1)",
            }}
          >
            <CertificateBody course={course} recipientName={recipientName} />
            {/* Glass sheen on hover */}
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
              style={{
                background: "linear-gradient(115deg, transparent 35%, rgba(255,255,255,0.35) 50%, transparent 65%)",
              }}
            />
          </div>

          {/* "Tap to preview" overlay */}
          <div
            className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-950/85 backdrop-blur-md border border-cyan-500/40 text-cyan-200 text-[11px] font-bold shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            data-testid="cert-mockup-cta"
          >
            <ZoomIn size={11} /> Tap to preview
          </div>
        </button>

        {/* Caption row */}
        <div className="mt-4 flex items-start gap-2.5">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 border" style={{ background: "rgba(34,211,238,0.12)", borderColor: "rgba(34,211,238,0.35)" }}>
            <Award size={14} className="text-cyan-300" />
          </div>
          <div>
            <p className="text-white text-sm font-bold leading-tight">Earn a verified certificate</p>
            <p className="text-zinc-500 text-[12px] leading-relaxed">
              Shareable on LinkedIn · Issued by <span className="text-cyan-300">shansoai.com</span> · Your name printed exactly as you sign up.
            </p>
          </div>
        </div>
      </div>

      {/* Float keyframes */}
      <style>{`
        @keyframes cert-float {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }
      `}</style>

      {/* ─── Flat preview modal ─── */}
      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-8"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
          data-testid="cert-mockup-modal"
        >
          <div
            className="relative w-full max-w-5xl"
            style={{ aspectRatio: "1.414 / 1", containerType: "inline-size" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute inset-0 rounded-2xl overflow-hidden border border-cyan-500/30 shadow-2xl">
              <CertificateBody course={course} recipientName={recipientName} />
            </div>
            <button
              onClick={() => setOpen(false)}
              className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-zinc-900 border border-cyan-500/40 text-white hover:bg-zinc-800 flex items-center justify-center shadow-lg"
              data-testid="cert-mockup-modal-close"
              aria-label="Close certificate preview"
            >
              <X size={16} />
            </button>
            <p className="absolute -bottom-9 left-0 right-0 text-center text-zinc-400 text-xs">
              Sample preview · Your name is printed when you complete the course.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
